<template>
	<ViewLayout>
		<template #header-title>
			Data Subject Matching Rules
		</template>
		<template #header-after>
			<Tabs
				:tab.sync="tab"
				:tabs="tabs"
			/>
		</template>
		<template #content>
			<div class="d-flex flex-row cassie-horizontal-md">
				<v-spacer />
				<PrimaryActionButton
					v-if="userFullPermissions && rulesLoaded"
					test-id="matching-rules-add-pass"
					@click="addPassToCurrentTab"
				>
					<v-icon left>
						mdi-plus
					</v-icon>
					{{ 'Create' | useLabels }}
				</PrimaryActionButton>
			</div>
			<v-alert
				dense
				text
				color="primary"
				icon="mdi-information-outline"
				class="mb-4 cassie-help-banner"
				role="region"
				aria-label="About passes"
			>
				<p class="mb-2 font-weight-medium">
					{{ 'About passes' | useLabels }}
				</p>
				<p class="mb-2">
					A pass is a set of rules evaluated together. Syrenis runs passes in order; the first pass whose rules all match decides what happens to the incoming record.
				</p>
				<!-- eslint-disable vue/singleline-html-element-content-newline -->
				<p class="mb-0">
					A rule's match <strong>Behavior</strong> controls how blank values are handled and allows you to include negative constraints on your rules. <strong>Update Details</strong> is set per pass and controls whether a successful match within that pass updates other contact details from the incoming record.
				</p>
				<!-- eslint-enable vue/singleline-html-element-content-newline -->
			</v-alert>
			<TabItems
				:tab="tab"
				:tabs="tabs"
			>
				<template #0>
					<PassList
						v-if="rulesLoaded"
						ref="apiPassList"
						:match-rules="apiSubmissionWorking"
						:field-name-items="fieldNameItems"
						:user-full-permissions="userFullPermissions"
						param-type="api"
						@change="onPassListChange('api', $event)"
					/>
				</template>
				<template #1>
					<PassList
						v-if="rulesLoaded"
						ref="loadPassList"
						:match-rules="dataLoaderWorking"
						:field-name-items="fieldNameItems"
						:user-full-permissions="userFullPermissions"
						param-type="load"
						@change="onPassListChange('load', $event)"
					/>
				</template>
				<template #2>
					<PassList
						v-if="rulesLoaded"
						ref="widgetPassList"
						:match-rules="preferenceWidgetsWorking"
						:field-name-items="fieldNameItems"
						:user-full-permissions="userFullPermissions"
						param-type="widget"
						@change="onPassListChange('widget', $event)"
					/>
				</template>
			</TabItems>

			<ConfirmSaveChangesModal
				v-if="showSaveChangesModal"
				entity-name="Changes"
				entity-type="Data Subject Matching Rules"
				@cancel="showSaveChangesModal = false"
				@save-changes="performSave"
			/>
		</template>
		<template #footer>
			<PageActionRow v-if="hasUnsavedChanges">
				<template #actions>
					<v-spacer />
					<div class="d-flex flex-row cassie-horizontal-md">
						<SecondaryActionButton @click="cancelChanges">
							{{ 'Cancel' | useLabels }}
						</SecondaryActionButton>
						<PrimaryActionButton
							v-if="userFullPermissions"
							@click="saveChanges"
						>
							{{ 'Save' | useLabels }}
						</PrimaryActionButton>
					</div>
				</template>
			</PageActionRow>
		</template>
	</ViewLayout>
</template>

<script>
import { mapGetters } from 'vuex'
import Tabs from '../../../../../../shared/components/tabs.vue'
import TabItems from '../../../../../../shared/components/tab-items.vue'
import ViewLayout from '../../../../../../shared/layouts/view-layout.vue'
import SecondaryActionButton from '../../../../../../shared/components/secondary-action-button.vue'
import PrimaryActionButton from '../../../../../../shared/components/primary-action-button.vue'
import PageActionRow from '../../../../../../shared/components/page-action-row.vue'
import ConfirmSaveChangesModal from '../../../../../../shared/components/confirm-save-changes-modal.vue'
import PassList from './components/PassList.vue'
import { showSnackbar } from '../../../../../../shared/state/snackbar.js'
import { MATCHING_RULES_FULL_PERMISSIONS } from '../../../../../../shared/permissions/admin-portal-permissions.js'
import { getRules, getDataSubjectContactFields, upsertMatchingRules } from '../../../../../../shared/utils/api/data-subject-rules.js'

export default {
	components: {
		SecondaryActionButton,
		PrimaryActionButton,
		ViewLayout,
		TabItems,
		Tabs,
		PassList,
		PageActionRow,
		ConfirmSaveChangesModal
	},
	data () {
		return {
			tabs: [
				{ title: 'API Submission', slot: '0', icon: 'mdi-resistor-nodes' },
				{ title: 'File Loader', slot: '1', icon: 'mdi-database-import-outline' },
				{ title: 'Embedded Consent Collection', slot: '2', icon: 'mdi-format-list-checkbox' }
			],
			tab: '0',
			showSaveChangesModal: false,
			apiSubmission: [],
			dataLoader: [],
			preferenceWidgets: [],
			apiSubmissionWorking: [],
			dataLoaderWorking: [],
			preferenceWidgetsWorking: [],
			dataSubjectContactFields: [],
			rulesLoaded: false
		}
	},
	computed: {
		...mapGetters('auth', ['productAreaPermission']),
		userFullPermissions () { return this.productAreaPermission(MATCHING_RULES_FULL_PERMISSIONS) },
		fieldNameItems () { return this.dataSubjectContactFields.map(f => f.columnName) },
		hasUnsavedChanges () {
			return JSON.stringify(this.apiSubmission) !== JSON.stringify(this.apiSubmissionWorking) ||
				JSON.stringify(this.dataLoader) !== JSON.stringify(this.dataLoaderWorking) ||
				JSON.stringify(this.preferenceWidgets) !== JSON.stringify(this.preferenceWidgetsWorking)
		}
	},
	async created () {
		await Promise.all([
			this.loadFieldOptions(),
			this.loadAllRules()
		])
	},
	methods: {
		async loadFieldOptions () {
			const data = await getDataSubjectContactFields()
			this.dataSubjectContactFields = data.standakFields
		},
		async loadAllRules () {
			await Promise.all([
				this.loadFor('api', 'apiSubmission'),
				this.loadFor('load', 'dataLoader'),
				this.loadFor('widget', 'preferenceWidgets')
			])
			this.rulesLoaded = true
		},
		async loadFor (paramType, key) {
			const { matchRules } = await getRules(paramType)
			const stripped = (matchRules ?? []).map(({ index, ...r }) => r)
			this[key] = stripped
			this[`${key}Working`] = JSON.parse(JSON.stringify(stripped))
		},
		onPassListChange (paramType, newRules) {
			const map = { api: 'apiSubmissionWorking', load: 'dataLoaderWorking', widget: 'preferenceWidgetsWorking' }
			this[map[paramType]] = newRules
		},
		cancelChanges () {
			this.apiSubmissionWorking = JSON.parse(JSON.stringify(this.apiSubmission))
			this.dataLoaderWorking = JSON.parse(JSON.stringify(this.dataLoader))
			this.preferenceWidgetsWorking = JSON.parse(JSON.stringify(this.preferenceWidgets))
		},
		saveChanges () {
			this.showSaveChangesModal = true
		},
		addPassToCurrentTab () {
			const refByTab = { 0: 'apiPassList', 1: 'loadPassList', 2: 'widgetPassList' }
			const targetRef = this.$refs[refByTab[this.tab]]
			if (targetRef && typeof targetRef.addPass === 'function') {
				targetRef.addPass()
			}
		},
		async performSave () {
			const tasks = []
			if (JSON.stringify(this.apiSubmission) !== JSON.stringify(this.apiSubmissionWorking)) {
				tasks.push(upsertMatchingRules(this.apiSubmissionWorking, 'api'))
			}
			if (JSON.stringify(this.dataLoader) !== JSON.stringify(this.dataLoaderWorking)) {
				tasks.push(upsertMatchingRules(this.dataLoaderWorking, 'load'))
			}
			if (JSON.stringify(this.preferenceWidgets) !== JSON.stringify(this.preferenceWidgetsWorking)) {
				tasks.push(upsertMatchingRules(this.preferenceWidgetsWorking, 'widget'))
			}
			try {
				await Promise.all(tasks)
				this.showSaveChangesModal = false
				await this.loadAllRules()
				showSnackbar('Changes saved successfully')
				window.scrollTo(0, 0)
			} catch (err) {
				this.showSaveChangesModal = false
				// Reload anyway so the view reflects whatever partial state landed
				await this.loadAllRules()
				showSnackbar('Failed to save changes. Please try again.')
			}
		}
	}
}
</script>
